import * as uuid from "uuid";
import { endpoints } from "@/configs/Settings";
import { GetSchedule } from "@/services/fhir";
import { HTTPResponseStatus } from "@/utils/enum";
import { useAuth } from "@/utils/useAuth";
import { useAxios } from "@/utils/useAxios";
import { TELECONSULTORIA_SERVICE_CATEGORY } from "../constants";
import { actorByType, isTeleconsultoriaSchedule } from "../utils/actors";
import { schedulesFromBundle } from "./teleconsultoriaScheduleService";

async function reuseHealthcareService(params: {
  practitionerRef: string;
  organizationToken: string;
}): Promise<{ id: string; display: string } | undefined> {
  const bundle = await GetSchedule({
    practitioner: params.practitionerRef,
    organization: params.organizationToken,
    includeHealthcareService: true,
    count: 30,
  });

  const clinical = schedulesFromBundle(bundle).find(
    (schedule) => !isTeleconsultoriaSchedule(schedule),
  );
  const actor = actorByType(clinical?.actor, "HealthcareService");
  const id = actor?.reference.split("/")[1];
  if (!id) return undefined;
  return { id, display: actor?.display || "Teleconsultoria" };
}

async function putMinimalHealthcareService(params: {
  organizationToken: string;
  name: string;
}): Promise<{ id: string; display: string }> {
  const id = uuid.v1();
  const url = `${endpoints.WORKFLOWFHIR}/HealthcareService/${id}`;
  const body = {
    resourceType: "HealthcareService",
    id,
    active: true,
    name: params.name,
    // Só o HCS criado aqui é marcado. Ele pode ser desativado no cancelamento da extensão.
    category: [{ coding: [{ ...TELECONSULTORIA_SERVICE_CATEGORY }] }],
    providedBy: {
      reference: `Organization/${params.organizationToken}`,
    },
  };
  const headers = {
    Accept: "*/*",
    Authorization: await useAuth.getUserToken(),
    "Content-Type": "application/fhir+json",
  };
  const response = await useAxios.put(url, JSON.stringify(body), {
    headers,
    timeout: 20000,
  });
  if (
    response.status !== HTTPResponseStatus.Success &&
    response.status !== HTTPResponseStatus.Created
  ) {
    throw new Error(`HealthcareService teleconsultoria: ${response.status}`);
  }
  return { id, display: params.name };
}

/**
 * Agenda só de disponibilidade (Practitioner + HealthcareService).
 * Sem Location, para não ser escolhida no fallback da evolução.
 */
export async function createTeleconsultoriaSchedule(params: {
  practitionerRef: string;
  practitionerDisplay: string;
  organizationToken: string;
  specialtyName?: string;
}): Promise<fhir5.Schedule> {
  const hcs =
    (await reuseHealthcareService(params)) ||
    (await putMinimalHealthcareService({
      organizationToken: params.organizationToken,
      name: params.specialtyName || "Teleconsultoria",
    }));

  const id = uuid.v1();
  const url = `${endpoints.WORKFLOWFHIR}/Schedule/${id}`;
  const resource = {
    id,
    resourceType: "Schedule",
    active: true,
    name: "Teleconsultoria",
    serviceCategory: [
      {
        coding: [
          {
            system: TELECONSULTORIA_SERVICE_CATEGORY.system,
            code: TELECONSULTORIA_SERVICE_CATEGORY.code,
            display: TELECONSULTORIA_SERVICE_CATEGORY.display,
          },
        ],
        text: TELECONSULTORIA_SERVICE_CATEGORY.display,
      },
    ],
    actor: [
      {
        reference: params.practitionerRef,
        display: params.practitionerDisplay,
      },
      {
        reference: `HealthcareService/${hcs.id}`,
        display: hcs.display,
      },
    ],
  };

  const headers = {
    Accept: "*/*",
    Authorization: await useAuth.getUserToken(),
    "Content-Type": "application/fhir+json",
  };
  const response = await useAxios.put(url, JSON.stringify(resource), {
    headers,
    timeout: 20000,
  });
  if (
    response.status !== HTTPResponseStatus.Success &&
    response.status !== HTTPResponseStatus.Created
  ) {
    throw new Error(`Schedule teleconsultoria: ${response.status}`);
  }
  return (response.data || resource) as fhir5.Schedule;
}
